import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Award, Check, Star, Users, BadgeCheck, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import apiService from '../services/api';
import BrandLogo from '../components/BrandLogo';

const InfluencerCertificationPage = () => {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState('');
  const [applicationId, setApplicationId] = useState(null);
  
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    platform: '',
    handle: '',
    follower_count: '',
    category: '',
    website: '',
    bio: '',
    opt_in_directory: true  // Default to opted-in
  });

  useEffect(() => {
    loadPricing();
  }, []);

  const loadPricing = async () => {
    try {
      setIsLoading(true);
      const data = await apiService.getInfluencerPricing();
      setPricing(data);
    } catch (err) {
      console.error('Error loading pricing:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validation
    if (!formData.full_name || !formData.email || !formData.platform || 
        !formData.handle || !formData.follower_count || !formData.category) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await apiService.submitInfluencerApplication(formData);
      setApplicationId(response.application_id);
      setSubmitSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const platforms = [
    { value: 'instagram', label: 'Instagram' },
    { value: 'tiktok', label: 'TikTok' },
    { value: 'youtube', label: 'YouTube' },
    { value: 'twitter', label: 'X (Twitter)' },
    { value: 'linkedin', label: 'LinkedIn' },
    { value: 'facebook', label: 'Facebook' },
    { value: 'other', label: 'Other' }
  ];

  const followerRanges = [
    { value: '10k-50k', label: '10K - 50K' },
    { value: '50k-100k', label: '50K - 100K' },
    { value: '100k-500k', label: '100K - 500K' },
    { value: '500k-1m', label: '500K - 1M' },
    { value: '1m+', label: '1M+' }
  ];

  const categories = [
    { value: 'health', label: 'Health & Wellness' },
    { value: 'politics', label: 'Politics & News' },
    { value: 'finance', label: 'Finance & Business' },
    { value: 'tech', label: 'Technology' },
    { value: 'science', label: 'Science & Education' },
    { value: 'lifestyle', label: 'Lifestyle' },
    { value: 'entertainment', label: 'Entertainment' },
    { value: 'sports', label: 'Sports' },
    { value: 'other', label: 'Other' }
  ];

  if (submitSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
        <header className="border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-40">
          <div className="container mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <BrandLogo size="md" data-testid="brand-logo-header" />
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
                KaiCheckIt
              </span>
            </div>
          </div>
        </header>

        <main className="container mx-auto px-6 py-16">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-800 mb-4">Application Submitted!</h1>
            <p className="text-lg text-slate-600 mb-6">
              Thank you for applying for KaiCheckIt Influencer Certification.
            </p>
            <Card className="text-left mb-8">
              <CardContent className="pt-6 space-y-4">
                <div className="flex items-center gap-2">
                  <Badge className="bg-blue-100 text-blue-800">Application ID</Badge>
                  <span className="font-mono text-sm">{applicationId}</span>
                </div>
                <div className="space-y-2">
                  <p className="font-semibold text-slate-700">What happens next?</p>
                  <ul className="space-y-2 text-sm text-slate-600">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Our team will verify your social media credentials</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>You will receive an email within 5-7 business days</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Once approved, complete payment to receive your badge</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Badge will be issued within 24 hours of payment</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
            <Button onClick={() => navigate('/')} className="bg-blue-600 hover:bg-blue-700">
              Return to Home
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BrandLogo size="md" data-testid="brand-logo-header" />
            <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
              KaiCheckIt
            </span>
          </div>
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl mb-6 shadow-lg">
            <BadgeCheck className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-amber-600 via-amber-500 to-yellow-500 bg-clip-text text-transparent">
            KaiCheckIt Influencer Certification
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Stand out as a verified, trustworthy voice. Get your credentials verified and display the KaiCheckIt badge on your profile.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {/* Pricing Card */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24 border-2 border-amber-400 shadow-xl">
              <CardHeader className="bg-gradient-to-br from-amber-400 to-amber-600 text-white rounded-t-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Award className="w-6 h-6" />
                  <CardTitle>Influencer Badge</CardTitle>
                </div>
                <CardDescription className="text-amber-100">
                  Verify your credentials, earn trust
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                {isLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
                  </div>
                ) : pricing ? (
                  <>
                    {/* Founding Member Banner */}
                    {pricing.promo_spots_remaining > 0 && (
                      <div className="bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-lg p-3 text-center mb-4">
                        <div className="font-bold text-sm">⭐ FOUNDING MEMBER</div>
                        <div className="text-xs">40% OFF for first 50 influencers!</div>
                        <div className="text-xs mt-1 opacity-90">{pricing.promo_spots_remaining} spots remaining</div>
                      </div>
                    )}
                    
                    <div className="text-center space-y-4">
                      {/* Promo Yearly - Only for first 50 */}
                      {pricing.promo_spots_remaining > 0 ? (
                        <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-4">
                          <div className="text-xs text-amber-700 font-semibold mb-1">FOUNDING MEMBER RATE</div>
                          <div className="text-3xl font-bold text-amber-700">
                            ${pricing.promo_yearly_price}
                            <span className="text-base font-normal text-amber-600">/year</span>
                          </div>
                          <div className="text-xs text-slate-500 mt-1">
                            <span className="line-through">${pricing.yearly_price}/year</span>
                            <span className="text-green-600 ml-1">Save $80!</span>
                          </div>
                          <div className="text-xs text-amber-600 mt-2">
                            Rate locked while subscription active
                          </div>
                        </div>
                      ) : (
                        <>
                          {/* Regular Monthly */}
                          <div className="pb-3 border-b border-slate-200">
                            <div className="text-3xl font-bold text-slate-800">
                              ${pricing.monthly_price}
                              <span className="text-base font-normal text-slate-500">/month</span>
                            </div>
                          </div>
                          
                          {/* Regular Yearly */}
                          <div>
                            <div className="text-lg text-slate-600">
                              or <span className="font-bold">${pricing.yearly_price}/year</span>
                            </div>
                            <div className="text-xs text-amber-600 mt-1">Save $40 with yearly!</div>
                          </div>
                        </>
                      )}
                    </div>
                    
                    <div className="space-y-3 pt-4 border-t border-slate-200">
                      {pricing.benefits?.map((benefit, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-slate-600">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    Unable to load pricing
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Application Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-amber-600" />
                  Apply for Certification
                </CardTitle>
                <CardDescription>
                  Fill out the form below to start your verification process
                </CardDescription>
              </CardHeader>
              <CardContent>
                {error && (
                  <Alert variant="destructive" className="mb-6">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="full_name">Full Name *</Label>
                      <Input
                        id="full_name"
                        value={formData.full_name}
                        onChange={(e) => handleInputChange('full_name', e.target.value)}
                        placeholder="Your full name"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="your@email.com"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="platform">Primary Platform *</Label>
                      <Select 
                        value={formData.platform} 
                        onValueChange={(value) => handleInputChange('platform', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select platform" />
                        </SelectTrigger>
                        <SelectContent>
                          {platforms.map((p) => (
                            <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="handle">Social Handle *</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-2.5 text-slate-400">@</span>
                        <Input
                          id="handle"
                          value={formData.handle}
                          onChange={(e) => handleInputChange('handle', e.target.value)}
                          placeholder="yourhandle"
                          className="pl-8"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="follower_count">Follower Count *</Label>
                      <Select 
                        value={formData.follower_count} 
                        onValueChange={(value) => handleInputChange('follower_count', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select range" />
                        </SelectTrigger>
                        <SelectContent>
                          {followerRanges.map((r) => (
                            <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="category">Content Category *</Label>
                      <Select 
                        value={formData.category} 
                        onValueChange={(value) => handleInputChange('category', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((c) => (
                            <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="website">Website (Optional)</Label>
                    <Input
                      id="website"
                      type="url"
                      value={formData.website}
                      onChange={(e) => handleInputChange('website', e.target.value)}
                      placeholder="https://yourwebsite.com"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bio">Brief Bio (Optional)</Label>
                    <Textarea
                      id="bio"
                      value={formData.bio}
                      onChange={(e) => handleInputChange('bio', e.target.value)}
                      placeholder="Tell us about yourself and your content focus..."
                      rows={3}
                      maxLength={500}
                    />
                    <p className="text-xs text-slate-500">{formData.bio.length}/500 characters</p>
                  </div>

                  {/* Directory Opt-In Section */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <BadgeCheck className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-slate-700">
                          <p className="font-semibold mb-1">Optional Directory Listing</p>
                          <p className="text-slate-600">
                            As a KaiCheckIt Verified professional, you may choose to appear in the public directory.
                            This listing confirms verification status only and does not constitute endorsement.
                            Listings are removed automatically if verification expires.
                          </p>
                        </div>
                      </div>
                      <label className="flex items-center gap-3 cursor-pointer p-2 rounded hover:bg-blue-100 transition-colors">
                        <input
                          type="checkbox"
                          checked={formData.opt_in_directory}
                          onChange={(e) => handleInputChange('opt_in_directory', e.target.checked)}
                          className="w-4 h-4 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-slate-700">
                          I want my verified status listed in the KaiCheckIt Directory
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <Star className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-slate-700">
                        <p className="font-semibold mb-1">Verification Process</p>
                        <p>After submitting, our team will verify your credentials via Common Ninja integration. You'll receive an email with next steps within 5-7 business days.</p>
                      </div>
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white py-6 text-lg"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <BadgeCheck className="w-5 h-5 mr-2" />
                        Submit Application
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default InfluencerCertificationPage;
